'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Edit3,
  FileText,
  Save,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { AssessmentResultsSection } from './assessment-results-section';

// Using simple CSS transitions instead of complex animations for better compatibility

interface ClinicalSection {
  id: string;
  title: string;
  content: string;
  isCollapsed: boolean;
  isEditing: boolean;
}

interface ProfessionalClinicalNoteProps {
  content?: string;
  noteContent?: string;
  isStreaming?: boolean;
  streamingProgress?: number;
  onContentChange?: (content: string) => void;
  className?: string;
  sessionId?: string;
  isCompleteView?: boolean;
}

/**
 * Professional Clinical Note Component
 * Inspired by open-canvas but optimized for medical professionals
 * Features:
 * - WYSIWYG editing (no markdown exposure)
 * - Collapsible sections for better organization
 * - Professional medical document styling
 * - Medical-specific actions (save, export, print)
 */
export function ProfessionalClinicalNote({
  content,
  noteContent,
  isStreaming = false,
  streamingProgress = 0,
  onContentChange,
  className,
  sessionId,
  isCompleteView = false,
}: ProfessionalClinicalNoteProps) {
  const finalContent = content || noteContent || '';
  const [sections, setSections] = useState<ClinicalSection[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState('');
  const [lastSavedContent, setLastSavedContent] = useState('');
  // 首次加载的内容视为服务端已保存基线；自动保存只应针对其后的真实编辑
  const baselineInitializedRef = useRef(false);

  // Parse markdown content into structured sections
  useEffect(() => {
    if (!finalContent) {
      setSections([]);
      return;
    }

    const parsedSections = parseMarkdownToSections(finalContent);
    setSections(parsedSections);
    setEditingContent(finalContent);
  }, [finalContent]);

  useEffect(() => {
    if (!isCompleteView || !sessionId || !finalContent || finalContent === lastSavedContent) return;
    if (!baselineInitializedRef.current) {
      baselineInitializedRef.current = true;
      setLastSavedContent(finalContent);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/sessions/${sessionId}/save-note`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note_content: finalContent }),
        });
        if (response.ok) setLastSavedContent(finalContent);
      } catch (error) {
        console.error('Failed to auto-save clinical note:', error);
      }
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [finalContent, isCompleteView, lastSavedContent, sessionId]);

  const parseMarkdownToSections = (markdown: string): ClinicalSection[] => {
    const sections: ClinicalSection[] = [];
    const lines = markdown.split('\n');
    let currentSection: Partial<ClinicalSection> | null = null;
    let contentBuffer: string[] = [];

    for (const line of lines) {
      // Check for section headers (## 主诉, ## 现病史, etc.)
      if (line.startsWith('## ')) {
        // Save previous section
        if (currentSection) {
          sections.push({
            ...currentSection,
            content: contentBuffer.join('\n').trim(),
            isCollapsed: false,
            isEditing: false,
          } as ClinicalSection);
        }

        // Start new section
        const title = line.replace('## ', '').trim();
        currentSection = {
          id: generateSectionId(title, sections.length),
          title,
        };
        contentBuffer = [];
      } else if (currentSection) {
        contentBuffer.push(line);
      }
    }

    // Save last section
    if (currentSection) {
      sections.push({
        ...currentSection,
        content: contentBuffer.join('\n').trim(),
        isCollapsed: false,
        isEditing: false,
      } as ClinicalSection);
    }

    return sections;
  };

  const generateSectionId = (title: string, index: number): string => {
    // \w 不匹配中文，纯中文标题会生成空串 id，导致 React key 重复；
    // 改用 Unicode 字母数字类别生成 slug，并附加序号保证唯一。
    const slug = title
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, '')
      .trim()
      .replace(/\s+/g, '-');
    return slug ? `${slug}-${index}` : `section-${index}`;
  };

  const toggleSectionCollapse = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, isCollapsed: !section.isCollapsed } : section
      )
    );
  };

  const startEditingSection = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) => (section.id === sectionId ? { ...section, isEditing: true } : section))
    );
  };

  const saveEditingSection = (sectionId: string, newContent: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, content: newContent, isEditing: false } : section
      )
    );

    // Reconstruct full markdown and notify parent
    const updatedSections = sections.map((section) =>
      section.id === sectionId ? { ...section, content: newContent, isEditing: false } : section
    );

    const fullMarkdown = reconstructMarkdown(updatedSections);
    onContentChange?.(fullMarkdown);
  };

  const cancelEditingSection = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) => (section.id === sectionId ? { ...section, isEditing: false } : section))
    );
  };

  const reconstructMarkdown = (sections: ClinicalSection[]): string => {
    return sections.map((section) => `## ${section.title}\n${section.content}`).join('\n\n');
  };

  const handleSave = async () => {
    if (sessionId) {
      const response = await fetch(`/api/sessions/${sessionId}/save-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_content: finalContent }),
      });
      if (!response.ok) {
        toast.error('保存失败');
        return;
      }
      setLastSavedContent(finalContent);
    }
    toast.success('临床记录已保存');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(finalContent);
      toast.success('已复制到剪贴板');
    } catch (err) {
      toast.error('复制失败');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([finalContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `临床记录_${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('记录已下载');
  };

  const handlePrint = () => {
    window.print();
    toast.success('正在打印...');
  };

  if (!finalContent && !isStreaming) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <FileText className="mb-4 h-16 w-16 text-blue-300" />
        <h3 className="mb-2 text-lg font-semibold text-gray-700">开始生成临床记录</h3>
        <p className="text-gray-500">进行对话后点击"总结"按钮生成专业的临床记录</p>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full flex-col bg-white', className)}>
      {/* Header with Actions */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">临床记录</h2>
            {isStreaming && (
              <Badge variant="outline" className="text-blue-600">
                生成中...
              </Badge>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleCopy} disabled={isStreaming}>
              <Copy className="mr-1 h-4 w-4" />
              复制
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={isStreaming}>
              <Download className="mr-1 h-4 w-4" />
              导出
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={isStreaming}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="mr-1 h-4 w-4" />
              保存
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={isStreaming}>
              打印
            </Button>
          </div>
        </div>

        {/* Streaming Progress */}
        {isStreaming && (
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between text-sm text-gray-600">
              <span>正在生成临床记录...</span>
              <span>{streamingProgress}%</span>
            </div>
            <Progress value={streamingProgress} className="h-2" />
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        {/* Document Header */}
        <div className="border-b border-gray-200 pb-4 text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-900">临床心理评估记录</h1>
          <p className="text-sm text-gray-600">
            生成时间：
            {new Date().toLocaleDateString('zh-CN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>

        {/* Clinical Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div
              key={section.id}
              className="rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 ease-out"
            >
              {/* Section Header */}
              <div
                className="flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-gray-50"
                onClick={() => toggleSectionCollapse(section.id)}
              >
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                  {section.title.includes('主诉') && <Badge variant="secondary">必填</Badge>}
                </div>

                <div className="flex items-center space-x-2">
                  {!section.isCollapsed && !isStreaming && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingSection(section.id);
                      }}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  )}
                  {section.isCollapsed ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Section Content */}
              {!section.isCollapsed && (
                <div className="border-t border-gray-200 transition-all duration-200 ease-out">
                  <div className="p-4">
                    {section.title.includes('评估结果') && !section.isEditing ? (
                      <AssessmentResultsSection content={section.content} />
                    ) : section.isEditing ? (
                      <EditableContent
                        content={section.content}
                        onSave={(content) => saveEditingSection(section.id, content)}
                        onCancel={() => cancelEditingSection(section.id)}
                      />
                    ) : (
                      <div className="prose prose-sm max-w-none">
                        <div
                          className="leading-relaxed whitespace-pre-wrap text-gray-700"
                          dangerouslySetInnerHTML={{
                            __html: formatContentForDisplay(section.content),
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Streaming indicator */}
        {isStreaming && (
          <div className="py-8 text-center">
            <div className="inline-flex items-center rounded-full bg-blue-50 px-4 py-2">
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
              <span className="font-medium text-blue-600">正在生成临床记录...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Editable Content Component for inline editing
function EditableContent({
  content,
  onSave,
  onCancel,
}: {
  content: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}) {
  const [editValue, setEditValue] = useState(content);

  return (
    <div className="space-y-3">
      <textarea
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        className="min-h-[200px] w-full resize-none rounded-md border border-gray-300 p-3 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
        placeholder="编辑内容..."
        autoFocus
      />
      <div className="flex justify-between space-x-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          <X className="mr-1 h-4 w-4" />
          取消
        </Button>
        <Button
          size="sm"
          onClick={() => onSave(editValue)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Check className="mr-1 h-4 w-4" />
          保存
        </Button>
      </div>
    </div>
  );
}

// Format content for display (convert simple markdown to HTML)
function formatContentForDisplay(content: string): string {
  if (!content) return '';

  // 先转义 HTML，防止病历文本中的标签/属性经 dangerouslySetInnerHTML 注入
  let formatted = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Simple markdown replacements
  formatted = formatted.replace(/^\*\*(.*?)\*\*/gm, '<strong>$1</strong>');
  formatted = formatted.replace(/\*\s(.+)/g, '<li>$1</li>');
  formatted = formatted.replace(/\n\s*\n/g, '<br/><br/>');

  return formatted;
}
